/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChevronDown16 } from '@carbon/icons-react';
import { Col, Row, Tabs } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import styled, { css } from 'styled-components';
import { ConversationAPI, UserMessage } from 'api/conversations';
import { UserType } from 'api/models';
import { PinScopeType, PinResourceType, Spotlight } from 'api/spotlight/types';
import {
  FieldOption,
  StatusGroup,
  Task,
  TaskAPI,
  TaskSourceType,
} from 'api/tasks';
import {
  CpBody01Link,
  CpHeading04Primary,
} from 'components/atoms/customer-portal/typography';
import { CustomDueDatePicker } from 'components/atoms/due-date-picker';
import { AssigneeTrigger } from 'components/molecules/squad-picker/trigger';
import {
  optionsToSquadPayload,
  squad,
  squadToOptions,
} from 'components/molecules/squad-picker/utils';
import { TaskDescription } from 'components/molecules/tasks/details/description';
import { ConversationPane } from 'components/organisms/conversations';
import { MessagesComment } from 'components/organisms/conversations/messages-comment';
import { RbacTaskAssigneePicker } from 'components/organisms/rbac/components/rbac-assignee-pickers';
import { SpotlightForTaskDrawer } from 'components/organisms/spotlight/spotlight-for-task';
import { SpotlightGroup } from 'components/organisms/spotlight/spotlight-in-tag';
import { TaskApprovals } from 'components/organisms/task-pane/task-approvals';
import { TaskFormRenderer } from 'components/organisms/task-pane/task-detail/forms';
import { TaskTabKeys } from 'components/organisms/task-pane/task-detail/task-detail';
import { useSelectedStatusOption, useStatusField } from 'data/fields/hooks';
import { useIsResponsive, useIsTablet } from 'hooks/use-media-query';
import { TaskDrawerTab } from 'hooks/use-navigate-to-task';
import { useTaskDrawerQueryParamsFromRouter } from 'hooks/use-query-params';
import {
  useGetSpotlights,
  useGetPinsByResourceAndScope,
  useDeleteMappingParentAndResource,
  useAddPinsOnResourceAndScope,
} from 'hooks/use-spotlight';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useAccountDataQuery } from 'queries/account';
import { tryGetTextFromHTML } from 'utils/getTextFromHTML';
import { truncate } from 'utils/truncate';
import { useCustomerPortalHashMap } from '../../utils';
import { FullWidthMarkAsStatus } from '../../widgets/common';
import { CpAlert, CpAlertType } from '../cp-alert';
import { CpTaskRespAssigneePicker } from '../cp-task-resp-assignee-picker';
import { CpPopover } from '../modal/cp-popover';
import { CpFullWidthCompletedTrigger } from '../status-picker/completed-trigger';
import { CpStatusPicker } from '../status-picker/cp-status-picker';
import { EazyStatusSwitchLabel } from '../status-picker/easy-status-switch-label';
import { CpColorTag } from '../tags/cp-tags';
import { CpParaRegular } from '../typography/typo';
import {
  SectionName,
  SectionWrapper,
  TitleWrapper,
} from './sections/components';
import { CpTaskDependentTasksSection } from './sections/cp-dependent-tasks-section';
import { CpTaskSubTasksSection } from './sections/cp-subtasks-section';
import { CpTaskAttachmentsSection } from './sections/cp-task-attachments-section';
import { CpTaskMoreDetailsSection } from './sections/cp-task-more-details-section';
import { CpTaskDrawerFieldAndValuesSection } from './task-drawer-field-section';

const OuterWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  overflow-y: hidden;
  position: relative;
`;

export const StyledTabs = styled(Tabs)`
  &,
  .ant-tabs-content-holder,
  .ant-tabs-content {
    height: 100%;
  }
  .ant-tabs-nav {
    margin: 0px;
  }
  .ant-tabs-tabpane {
    overflow: hidden;
  }

  .rl-form-footer-wrapper {
    position: unset;
    width: 100%;
  }
`;

const StyledTabsWithSticky = styled(StyledTabs)<{ hasSpotlight: boolean }>`
  max-height: calc(
    100% - ${({ hasSpotlight }) => (hasSpotlight ? '60px' : '72px')}
  );
  @media (max-width: 768px) {
    max-height: calc(
      100% - ${({ hasSpotlight }) => (hasSpotlight ? '54px' : '66px')}
    );
  }
  @media (max-width: 480px) {
    max-height: calc(
      100% - ${({ hasSpotlight }) => (hasSpotlight ? '40px' : '62px')}
    );
  }
  .ant-tabs-nav {
    z-index: var(--zindex-layer-08);
    background-color: var(--white-100);
  }
  .ant-tabs-tabpane.ant-tabs-tabpane-active {
    overflow-y: auto;

    /* hide scrollbar  */
    &::-webkit-scrollbar {
      display: none;
    }

    &::-webkit-scrollbar-thumb {
      display: none;
    }

    &::-webkit-scrollbar-track {
      display: none;
    }
  }
`;

const DetailsWrapper = styled.div<{
  hasSpotlight?: boolean;
  backgroundTop: number;
}>`
  width: 768px;
  height: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: start;

  @media (max-width: 768px) {
    width: 100%;
  }
  ${({ hasSpotlight }) =>
    hasSpotlight
      ? css`
          background: linear-gradient(180deg, #ffe6cc 0%, #ffffff 100%);
        `
      : ''};

  background-size: 768px 128px;
  background-repeat: no-repeat;
  background-position: 0 ${({ backgroundTop }) => `-${backgroundTop}px`};
`;

const TaskSpotlightWrapper = styled.div<{ noMarginBottom: boolean }>`
  margin-bottom: ${({ noMarginBottom }) =>
    noMarginBottom ? '0px' : 'var(--cp-spacing-05)'};
  background: var(--cp-spotlight-task-drawer-gradient);
`;

const TaskName = styled(CpHeading04Primary)`
  cursor: pointer;
  margin-bottom: 0;
`;
const TaskNameWrapper = styled.div<{
  reduceMargin?: boolean;
  minimisedTaskName: boolean;
}>`
  margin-bottom: ${(props) =>
    props.reduceMargin ? 'var(--cp-spacing-05)' : 'var(--cp-spacing-07)'};
  position: sticky;
  top: 0;
  z-index: 110;
  background: white;
  ${TaskName} {
    transition: font-size 0.3s ease-in-out;
  }
  ${({ minimisedTaskName }) =>
    minimisedTaskName
      ? css`
          ${TaskName} {
            font-size: var(--cp-heading-6-font-size);
            padding-bottom: var(--cp-spacing-05);
          }
        `
      : ''};
`;

const BreadCrumbWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cp-spacing-02);
`;

const PhaseName = styled(CpBody01Link)`
  cursor: pointer;
  margin-bottom: 0;
`;

const AlertWrapper = styled.div`
  width: 100%;
  margin-bottom: var(--spacing-07);
`;

const BaseInfo = styled.div`
  margin-bottom: var(--spacing-06);
  margin-top: var(--spacing-06);

  [data-cy='choose.assignee'] {
    margin-bottom: var(--spacing-03);
    padding: 0;
  }
  span[value*='user'] {
    margin: 0 !important;
  }
`;

const ChatWrapper = styled.div`
  width: 480px;
  overflow: hidden;
  height: 100%;
  border-left: 1px solid var(--border-color);
  /* margin-left: var(--spacing-06); */

  @media (max-width: 768px) {
    display: none;
  }

  padding: var(--spacing-05) 0;

  @media (max-width: 768px) {
    padding-right: var(--spacing-04);
    padding-left: var(--spacing-04);
    padding-top: var(--spacing-06);
    padding-bottom: var(--spacing-06);
  }

  @media (max-width: 480px) {
    padding-right: var(--spacing-03);
    padding-left: var(--spacing-03);
  }
`;

const ChatAndApprovalsWrapper = styled(ChatWrapper)`
  overflow-y: auto;
`;

const StatusTriggerWrapper = styled.div`
  width: 200px;
  position: relative;
  cursor: pointer;
  padding-left: var(--cp-spacing-04);
  ${CpColorTag} {
    width: fit-content;
    min-width: fit-content;
  }
  :hover {
    background: var(--cp-gray-10);
  }
`;

const ChevronDownWrapperForStatusTrigger = styled.div`
  position: absolute;
  right: var(--cp-spacing-04);
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledRow = styled(Row)`
  margin-bottom: var(--spacing-07);
  @media (max-width: 480px) {
    margin-bottom: var(--spacing-06);
  }

  .ck.ck-content.ck-editor__editable.ck-rounded-corners.ck-editor__editable_inline.ck-read-only {
    padding: 0 var(--cp-spacing-05);
    border: none;
  }

  [class*='editor_description__'] {
    --rte-min-height: 0;
    outline-color: transparent !important;
    box-shadow: none !important;
    font-size: var(--cp-body-01-font-size);
  }
`;
const SpotlightRow = styled(Row)`
  margin-bottom: var(--spacing-06);
  @media (max-width: 480px) {
    margin-bottom: var(--spacing-05);
  }
`;

const Content = styled.div<{ minimisedTaskName: boolean }>`
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  padding-top: var(--cp-spacing-07);

  padding-right: var(--spacing-09);
  padding-left: var(--spacing-09);
  padding-bottom: var(--spacing-10);

  @media (max-width: 768px) {
    padding-right: var(--spacing-06);
    padding-left: var(--spacing-06);
    padding-bottom: var(--spacing-06);
  }

  @media (max-width: 480px) {
    padding-right: var(--spacing-05);
    padding-left: var(--spacing-05);
  }
  @media (max-width: 768px) {
    padding-bottom: var(--cp-spacing-08);
  }
  ${({ minimisedTaskName }) =>
    minimisedTaskName
      ? css`
          &:before {
            content: '';
            position: sticky;
            background: white;
            height: 32px;
            width: 100%;
            top: -32px;
            display: block;
            z-index: 2;
          }
        `
      : ''};
`;

const AssigneeTriggerWrapper = styled.div`
  width: 200px;
  height: 40px;
  /* margin-bottom: var(--spacing-06); */
  padding-left: var(--cp-spacing-04);
  display: flex;
  align-items: center;
  justify-content: start;
  font-size: var(--cp-body-01-font-size);
  * {
    font-size: var(--cp-body-01-font-size);
  }

  .ant-tag {
    padding: var(--cp-spacing-02);
    padding-right: var(--cp-spacing-03);
    padding-left: var(--cp-spacing-01);
    max-width: 150px;
  }

  :hover {
    background: var(--cp-gray-10);
  }

  ${CpBody01Link} {
    margin-bottom: 0;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const BaseInfoCol = styled(Col)`
  display: flex;
  align-items: center;

  [data-cy='choose.due.date'] {
    width: 200px;
    border-radius: 0;
    padding-left: var(--cp-spacing-04);
    font-size: var(--cp-body-01-font-size);

    :hover {
      background: var(--cp-gray-10);
    }
  }
`;

const DatePicker = styled(CustomDueDatePicker)``;

const BaseInfoRow = styled(Row)`
  height: 40px;
  margin-bottom: var(--spacing-03);
`;

const Footer = styled.div`
  width: 100%;
  background: white;
`;

export const CustomerPortalTaskDrawer: React.FC<{
  task: Task;
  refetch?: () => void;
}> = ({ task, refetch }) => {
  const isTablet = useIsTablet();
  const currentUser = useUser();
  const [dirty, setDirty] = useState(task);
  const queryClient = useQueryClient();
  const { data: spotlightForThisTask } = useGetPinsByResourceAndScope({
    resourceType: PinResourceType.Task,
    resourceId: task.taskId,
    scopeId: task?.project?.projectId,
    scopeType: PinScopeType.Project,
  });
  const { user } = useUser();
  const { data: accountData } = useAccountDataQuery();

  const selectedStatusOption = useSelectedStatusOption(dirty?.fields ?? []);
  const statusField = useStatusField();
  const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();

  const { mutateAsync: deleteSpotlight } = useDeleteMappingParentAndResource();
  const { mutateAsync: createSpotlight } = useAddPinsOnResourceAndScope();
  const { data: spotlightForCommentsInTheTask, refetch: refetchSpotlights } =
    useGetSpotlights({
      resourceType: PinResourceType.Chat,

      scopeId: task?.taskId,
      scopeType: PinScopeType.Task,
    });

  const assigneeOptions = useMemo(
    () => squadToOptions(squad(dirty?.assignee)),
    [dirty?.assignee],
  );

  const onEazyStatusUpdate = useCallback(() => {
    let fieldValue = selectedStatusOption?.value;

    switch (selectedStatusOption?.group) {
      case StatusGroup.Completed:
        return;

      case StatusGroup.Todo:
      default:
        fieldValue = 2;
        break;

      case StatusGroup.InProgress:
        fieldValue = 3;
        break;
    }

    const payload = {
      fields: [
        {
          fieldId: statusField?.fieldId,
          fieldValue,
        },
      ],
    };

    setDirty((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        fields: prev?.fields?.map((f) => {
          if (f?.fieldId === statusField?.fieldId) {
            return {
              ...f,
              fieldValue: fieldValue as number,
            };
          }

          return f;
        }),
      };
    });

    TaskAPI.updateTask(task?.project?.projectId, task?.taskId, payload).then(
      () => refetch?.(),
    );
  }, [
    refetch,
    selectedStatusOption?.group,
    selectedStatusOption?.value,
    statusField?.fieldId,
    task?.project?.projectId,
    task?.taskId,
  ]);

  const onStatusChange = useCallback(
    (change: Required<FieldOption<StatusGroup>>): void => {
      setDirty((prev) => {
        if (!prev) {
          return prev;
        }
        const value = change?.value;

        return {
          ...prev,
          fields: (prev?.fields ?? []).map((f) => {
            if (f?.fieldId === statusField?.fieldId) {
              return {
                ...f,
                fieldValue: value,
              };
            }

            return f;
          }),
        };
      });
      TaskAPI.updateTask(task?.project?.projectId, task?.taskId, {
        fields: [
          {
            fieldId: statusField?.fieldId,
            fieldValue: change?.value,
          },
        ],
      }).then(() => refetch?.());
    },
    [refetch, statusField?.fieldId, task?.project?.projectId, task?.taskId],
  );
  const translate = useTranslate();

  const handleDueDateChange = useCallback(
    ([_startDate, _dueDate]: [string | null, string | null]) => {
      setDirty((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          startDate: _startDate,
          dueDate: _dueDate,
        };
      });

      TaskAPI.updateTask(task?.project?.projectId, task?.taskId, {
        startDate: _startDate,
        dueDate: _dueDate,
      }).then(() => refetch?.());
    },
    [refetch, task?.project?.projectId, task?.taskId],
  );

  const onParentTaskBreadcrumbClick = useCallback(() => {
    const parentTaskId = task?.parentTaskObject?.taskId;

    if (parentTaskId) {
      openTaskDrawer(task?.project?.projectId, parentTaskId);
    }
  }, [
    openTaskDrawer,
    task?.parentTaskObject?.taskId,
    task?.project?.projectId,
  ]);

  useEffect(() => {
    setDirty(task);
  }, [task]);

  const isResponsive = useIsResponsive();

  const [assigneePickerModalVisible, setAssigneePickerModalVisible] =
    useState(false);

  const onAssigneeTriggerClick = useCallback(() => {
    if (isResponsive) {
      setAssigneePickerModalVisible(true);
    }
  }, [isResponsive]);

  const assigneeTriggerComponent = useMemo(
    () =>
      assigneeOptions?.length ? (
        <AssigneeTriggerWrapper onClick={onAssigneeTriggerClick}>
          <AssigneeTrigger size="small" value={assigneeOptions} />
        </AssigneeTriggerWrapper>
      ) : (
        <AssigneeTriggerWrapper onClick={onAssigneeTriggerClick}>
          <CpBody01Link>
            <FormattedMessage id="choose" />
          </CpBody01Link>
        </AssigneeTriggerWrapper>
      ),
    [assigneeOptions, onAssigneeTriggerClick],
  );

  const onAssigneePickerDone:
    | ((options: any, callback?: any) => void)
    | undefined = useCallback(
    (options) => {
      const payload = optionsToSquadPayload(options);

      setDirty((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          assignee: payload as typeof prev.assignee,
        };
      });

      if (isResponsive) {
        setAssigneePickerModalVisible(false);
      }

      TaskAPI.updateTask(task?.project?.projectId, task?.taskId, {
        assignee: payload,
      }).then(() => refetch?.());
    },
    [isResponsive, refetch, task?.project?.projectId, task?.taskId],
  );

  const renderMessagesComponent = useCallback(() => {
    if (user?.userType === UserType.CustomerPortalBot) {
      const accountId = accountData?.account?.accountId;

      if (!accountId) {
        return undefined;
      }

      return (
        <MessagesComment
          actionsOnLeft
          messageContent=""
          placeholder=""
          hidePrivate
          readonly
          handleUserMention={() => {}}
          accountId={accountId}
          projectId={task?.project?.projectId}
          onAddComment={() => {}}
          onClearReply={() => {}}
          hasAccessToCreatePrivateCommentsOnly={false}
        />
      );
    }

    return undefined;
  }, [
    accountData?.account?.accountId,
    task?.project?.projectId,
    user?.userType,
  ]);

  const basicDetails = useMemo(
    () => (
      <>
        <BaseInfo>
          {selectedStatusOption ? (
            <BaseInfoRow>
              <BaseInfoCol span={6}>
                <CpParaRegular>
                  <FormattedMessage id="Status" />
                </CpParaRegular>
              </BaseInfoCol>
              <BaseInfoCol span={18}>
                <CpPopover
                  getPopupContainer={() =>
                    document.getElementById('cp-modal-root') as HTMLElement
                  }
                  height={`${
                    (statusField?.metaData?.options?.length + 1) * 48
                  }px`}
                  trigger={['click']}
                  closeOnSelect
                  content={
                    <CpStatusPicker
                      type={dirty?.type}
                      dontStopPropagation
                      title={<FormattedMessage id="task_status" />}
                      onChange={onStatusChange}
                      options={statusField?.metaData?.options ?? []}
                      selectedOption={selectedStatusOption?.value}
                    />
                  }
                >
                  <StatusTriggerWrapper>
                    <CpColorTag color={selectedStatusOption?.color}>
                      {selectedStatusOption?.label}
                    </CpColorTag>
                    <ChevronDownWrapperForStatusTrigger>
                      <ChevronDown16 />
                    </ChevronDownWrapperForStatusTrigger>
                  </StatusTriggerWrapper>
                </CpPopover>
              </BaseInfoCol>
            </BaseInfoRow>
          ) : (
            <></>
          )}
          <BaseInfoRow>
            <BaseInfoCol span={6}>
              <CpParaRegular>
                <FormattedMessage id="assignee.users" />
              </CpParaRegular>
            </BaseInfoCol>
            <BaseInfoCol span={18}>
              {isResponsive ? (
                <>
                  {assigneeTriggerComponent}
                  <CpTaskRespAssigneePicker
                    visible={assigneePickerModalVisible}
                    onChange={onAssigneePickerDone}
                    projectId={task?.project?.projectId}
                    selectedOptions={assigneeOptions}
                    onClearAll={() => {
                      onAssigneePickerDone([]);
                    }}
                  />
                </>
              ) : (
                <RbacTaskAssigneePicker
                  projectId={['project_id', task?.project?.projectId]}
                  dueDate={task?.dueDate}
                  startDate={task?.startDate}
                  onDone={onAssigneePickerDone}
                  value={assigneeOptions}
                  hidePlaceholderCreate
                  hidePlaceholders
                  hideHoverBgOnButtonWrapper
                  triggerComponent={assigneeTriggerComponent}
                  popoverProps={{
                    getPopupContainer: () =>
                      document.getElementById('cp-modal-root') as HTMLElement,
                  }}
                />
              )}
            </BaseInfoCol>
          </BaseInfoRow>
          <BaseInfoRow>
            <BaseInfoCol span={6}>
              <CpParaRegular>
                <FormattedMessage id="due_date" />
              </CpParaRegular>
            </BaseInfoCol>
            <BaseInfoCol span={18}>
              <DatePicker
                hideIcon
                startDate={dirty?.startDate}
                dueDate={dirty?.dueDate}
                onDateChange={handleDueDateChange}
              />
            </BaseInfoCol>
          </BaseInfoRow>
        </BaseInfo>
        <StyledRow>
          <CpTaskDrawerFieldAndValuesSection
            task={task}
            refetchTask={refetch}
          />
        </StyledRow>
        {tryGetTextFromHTML(task?.taskDescription)?.trim() ? (
          <StyledRow>
            <Col span={24}>
              <SectionWrapper>
                <TitleWrapper>
                  <SectionName>
                    <FormattedMessage id="description" />
                  </SectionName>
                </TitleWrapper>
                <TaskDescription.Editor
                  hideActionBar
                  disabled
                  disableSmartfills
                  updateTask={(taskDescription) => {
                    TaskAPI.updateTask(task?.project?.projectId, task?.taskId, {
                      taskDescription,
                    }).then(() => refetch?.());
                  }}
                  projectId={task?.project?.projectId}
                  taskDescription={task?.taskDescription}
                />
              </SectionWrapper>
            </Col>
          </StyledRow>
        ) : (
          <></>
        )}
        {!dirty?.attachments || dirty?.attachments.length === 0 ? (
          <></>
        ) : (
          <StyledRow>
            <Col span={24}>
              <CpTaskAttachmentsSection task={dirty} refetchTask={refetch} />
            </Col>
          </StyledRow>
        )}
        {!dirty?.subTasks || dirty?.subTasks.length === 0 ? (
          <></>
        ) : (
          <StyledRow>
            <Col span={24}>
              <CpTaskSubTasksSection task={dirty as any} />
            </Col>
          </StyledRow>
        )}
        {!dirty?.dependentTasks || dirty?.dependentTasks.length === 0 ? (
          <></>
        ) : (
          <StyledRow>
            <Col span={24}>
              <CpTaskDependentTasksSection task={dirty as any} />
            </Col>
          </StyledRow>
        )}
        <StyledRow>
          <Col span={24}>
            <CpTaskMoreDetailsSection
              task={dirty as any}
              refetchTask={refetch}
            />
          </Col>
        </StyledRow>
        <Footer>
          <Row>
            <Col span={24}>
              {selectedStatusOption &&
              selectedStatusOption?.group !== StatusGroup.Completed ? (
                <FullWidthMarkAsStatus onClick={onEazyStatusUpdate}>
                  <EazyStatusSwitchLabel
                    selectedOption={selectedStatusOption}
                  />
                </FullWidthMarkAsStatus>
              ) : (
                <CpFullWidthCompletedTrigger
                  selectedOption={selectedStatusOption}
                />
              )}
            </Col>
          </Row>
        </Footer>
      </>
    ),
    [
      assigneeOptions,
      assigneePickerModalVisible,
      assigneeTriggerComponent,
      dirty,
      handleDueDateChange,
      isResponsive,
      onAssigneePickerDone,
      onEazyStatusUpdate,
      onStatusChange,
      refetch,
      selectedStatusOption,
      statusField?.metaData?.options,
      task,
    ],
  );
  const taskNameWrapperRef = useRef<HTMLDivElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const [minimisedTaskName, setMinimisedTaskName] = useState(false);
  const [contentScrolledBy, setContentScrolledBy] = useState(0);
  const contentScrollManager = useCallback(() => {
    if (
      spotlightForThisTask?.deleted === false &&
      minimisedTaskName === false
    ) {
      setContentScrolledBy(contentWrapperRef.current?.scrollTop ?? 0);
    }

    if (task?.formInstanceId != null) {
      return;
    }
    const paddingFromTop = 32;
    const wrapperTop = taskNameWrapperRef.current?.offsetTop ?? 0;
    const currentScrollTop = contentWrapperRef.current?.scrollTop ?? 0;

    if (
      currentScrollTop !== 0 &&
      Math.floor(wrapperTop - paddingFromTop - currentScrollTop) <= 0
    ) {
      if (minimisedTaskName === false) {
        setMinimisedTaskName(true);
      }
    } else if (minimisedTaskName === true) {
      setMinimisedTaskName(false);
    }
  }, [
    contentWrapperRef,
    minimisedTaskName,
    spotlightForThisTask?.deleted,
    task?.formInstanceId,
    taskNameWrapperRef,
  ]);

  const convPane = useMemo(
    () => (
      <ConversationPane
        commentBoxConfig={{
          hidePrivate: true,
          hideSmartfills: true,
          className: 'rl-cp-chat-pane-message-input',
        }}
        messagesComponent={renderMessagesComponent()}
        spotlights={spotlightForCommentsInTheTask ?? []}
        context={{ type: 'TASK', id: task?.taskId }}
        projectId={task?.project?.projectId}
        accountId={currentUser.account.accountId}
        apiService={ConversationAPI}
        onRefetchSpotlights={refetchSpotlights}
        onStarMessage={(comment) => {
          const commentId = comment?.commentId ?? 0;
          const taskId = task?.taskId ?? 0;

          const sp = spotlightForCommentsInTheTask?.find(
            (s) => s.resourceId === commentId && s.resourceType === 'CHAT',
          );

          if (sp && sp?.deleted === false) {
            deleteSpotlight({
              scopeType: PinScopeType.Task,
              scopeId: taskId,
              resourceType: PinResourceType.Chat,
              resourceId: commentId,
            });
          } else {
            createSpotlight({
              scopeType: PinScopeType.Task,
              scopeId: taskId,
              resourceType: PinResourceType.Chat,
              resourceId: commentId,
              mapping: {
                caption: (comment as UserMessage)?.content ?? '',
                deleted: false,
                scopeType: PinScopeType.Task,
                scopeId: taskId,
                resourceType: PinResourceType.Chat,
                resourceId: commentId,
              } as unknown as Spotlight,
            }).then(() => refetchSpotlights());
          }
        }}
      />
    ),
    [
      createSpotlight,
      currentUser.account.accountId,
      deleteSpotlight,
      refetchSpotlights,
      renderMessagesComponent,
      spotlightForCommentsInTheTask,
      task?.project?.projectId,
      task?.taskId,
    ],
  );

  const { hashMap, setHashMap } = useCustomerPortalHashMap();

  const navigateTo = useCallback(
    (tab: TaskDrawerTab) => setHashMap('td_tab', tab),
    [setHashMap],
  );

  const navigateToApprovals = useCallback(() => {
    return navigateTo(TaskDrawerTab.Approvals);
  }, [navigateTo]);

  const navigateToConversations = useCallback(() => {
    return navigateTo(TaskDrawerTab.Conversations);
  }, [navigateTo]);

  const activeTab = useMemo(() => hashMap?.td_tab, [hashMap?.td_tab]);
  const canShowNewTaskApproval = useMemo(
    () => hashMap?.td_approvals === 'show',
    [hashMap?.td_approvals],
  );

  const hasApprovals = useMemo(
    () => (task?.approvals ?? []).length > 0,
    [task?.approvals],
  );
  const isApprovalTab = useMemo(
    () => activeTab === TaskDrawerTab.Approvals,
    [activeTab],
  );

  const canRenderApprovalTab = useMemo(
    () => isApprovalTab || hasApprovals,
    [hasApprovals, isApprovalTab],
  );

  const approvalData = useMemo(() => {
    return canShowNewTaskApproval || !hasApprovals
      ? {
          approvalId: 0,
          requestBy: currentUser.user,
          requestTo: [],
          private: false,
        }
      : undefined;
  }, [canShowNewTaskApproval, hasApprovals, currentUser]);

  const handleCancel = useCallback(() => {
    return hasApprovals ? navigateToApprovals() : navigateToConversations();
  }, [hasApprovals, navigateToApprovals, navigateToConversations]);

  const handleSubmit = useCallback(() => {
    navigateToApprovals();
    refetch?.();
  }, [navigateToApprovals, refetch]);

  const approvalsPane = useMemo(() => {
    if (task?.approvals?.length === 0) {
      return <></>;
    }

    const isDocumentTask =
      task?.source?.sourceType != null &&
      [TaskSourceType.DocumentSection, TaskSourceType.Document].includes(
        task?.source?.sourceType,
      );

    return (
      <TaskApprovals
        isPrivate={false}
        onNewApprovalCancel={handleCancel}
        onNewApprovalSubmit={handleSubmit}
        newData={approvalData}
        data={task?.approvals ?? []}
        taskId={task?.taskId}
        projectId={task?.project?.projectId}
        isDocumentTask={isDocumentTask}
      />
    );
  }, [
    approvalData,
    handleCancel,
    handleSubmit,
    task?.approvals,
    task?.project?.projectId,
    task?.source?.sourceType,
    task?.taskId,
  ]);
  const anySpotlightAvailable = useMemo(
    () =>
      spotlightForThisTask?.deleted === false ||
      (spotlightForCommentsInTheTask ?? [])?.length > 0,
    [spotlightForCommentsInTheTask, spotlightForThisTask?.deleted],
  );

  return (
    <OuterWrapper data-cy="cp.task_drawer">
      <DetailsWrapper
        hasSpotlight={spotlightForThisTask?.deleted === false}
        backgroundTop={contentScrolledBy}
      >
        <Content
          onScroll={contentScrollManager}
          ref={contentWrapperRef}
          minimisedTaskName={minimisedTaskName}
        >
          {anySpotlightAvailable ? (
            <SpotlightRow>
              <Col span={24}>
                {spotlightForThisTask?.deleted === false ? (
                  <TaskSpotlightWrapper
                    className="rl-cp-task-drawer-spotlight"
                    noMarginBottom={
                      (spotlightForCommentsInTheTask ?? [])?.length === 0
                    }
                  >
                    <SpotlightForTaskDrawer spotlight={spotlightForThisTask} />
                  </TaskSpotlightWrapper>
                ) : (
                  <></>
                )}
                {(spotlightForCommentsInTheTask ?? [])?.length > 0 ? (
                  <SpotlightGroup
                    onSpotlightClick={(s) => {
                      document
                        .querySelector(`[data-spotlight='${s?.resourceId}']`)
                        ?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'center',
                          inline: 'nearest',
                        });
                    }}
                    spotlights={spotlightForCommentsInTheTask ?? []}
                  />
                ) : (
                  <></>
                )}
              </Col>
            </SpotlightRow>
          ) : null}
          <TaskNameWrapper
            reduceMargin={!!task?.formInstanceId}
            ref={taskNameWrapperRef}
            minimisedTaskName={minimisedTaskName}
          >
            <BreadCrumbWrapper>
              <PhaseName>
                {truncate(task?.project.projectName, 25, 10)}{' '}
                <FormattedMessage id="/" />
              </PhaseName>
              {task?.parentTaskObject ? (
                <PhaseName onClick={onParentTaskBreadcrumbClick}>
                  {truncate(task?.parentTaskObject?.taskName, 25, 10) as string}
                  <FormattedMessage id="/" />
                </PhaseName>
              ) : (
                <></>
              )}
            </BreadCrumbWrapper>
            <TaskName data-cy="cp.task_drawer.task_name">
              {task?.taskName}
            </TaskName>
          </TaskNameWrapper>

          {task?.atRisk ? (
            <AlertWrapper>
              <CpAlert
                type={CpAlertType.Error}
                message={translate('task.at_risk')}
              />
            </AlertWrapper>
          ) : (
            <></>
          )}
          {task?.formInstanceId ? (
            <StyledTabsWithSticky
              hasSpotlight={anySpotlightAvailable}
              defaultActiveKey={
                task?.formInstanceId
                  ? TaskTabKeys.Form
                  : TaskTabKeys.BasicDetails
              }
            >
              <Tabs.TabPane
                tab={<FormattedMessage id="form" />}
                key={TaskTabKeys.Form}
                id="task_details.tabs.form"
              >
                <TaskFormRenderer
                  onSubmit={() => {
                    refetch?.();
                    if (task?.formInstanceId) {
                      queryClient.refetchQueries({
                        predicate: (query) =>
                          query?.queryHash?.includes('PROJECT_FORMS'),
                      });
                    }
                  }}
                  projectId={task.project?.projectId}
                  taskId={task.taskId}
                  formResponseCount={task.formDetails?.responseCount}
                  instanceId={task.formInstanceId}
                  responseId={
                    hashMap?.td_form_answer_id
                      ? parseInt(hashMap?.td_form_answer_id, 10)
                      : task.formDetails?.formAnswerId
                  }
                />
              </Tabs.TabPane>
              <Tabs.TabPane
                key={TaskTabKeys.BasicDetails}
                tab={<FormattedMessage id="basic_details" />}
              >
                {basicDetails}
              </Tabs.TabPane>
              {isTablet ? (
                <>
                  <Tabs.TabPane
                    key={TaskTabKeys.Chat}
                    tab={<FormattedMessage id="conversations" />}
                  >
                    {convPane}
                  </Tabs.TabPane>
                  {canRenderApprovalTab ? (
                    <Tabs.TabPane
                      key={TaskDrawerTab.Approvals}
                      tab={<FormattedMessage id="approvals" />}
                    >
                      {approvalsPane}
                    </Tabs.TabPane>
                  ) : (
                    <> </>
                  )}
                </>
              ) : (
                <></>
              )}
            </StyledTabsWithSticky>
          ) : isTablet ? (
            <StyledTabsWithSticky
              hasSpotlight={anySpotlightAvailable}
              defaultActiveKey={
                task?.formInstanceId
                  ? TaskTabKeys.Form
                  : TaskTabKeys.BasicDetails
              }
            >
              <Tabs.TabPane
                tab={<FormattedMessage id="basic_details" />}
                key={TaskTabKeys.BasicDetails}
              >
                {basicDetails}
              </Tabs.TabPane>

              <Tabs.TabPane
                key={TaskTabKeys.Chat}
                tab={<FormattedMessage id="conversations" />}
              >
                {convPane}
              </Tabs.TabPane>
            </StyledTabsWithSticky>
          ) : (
            basicDetails
          )}
        </Content>
      </DetailsWrapper>
      {!isTablet && !canRenderApprovalTab ? (
        <ChatWrapper>{convPane}</ChatWrapper>
      ) : (
        <></>
      )}

      {!isTablet && canRenderApprovalTab ? (
        <ChatAndApprovalsWrapper>
          <Tabs
            defaultActiveKey={hashMap?.td_tab ?? TaskTabKeys.Chat}
            activeKey={hashMap?.td_tab ?? TaskTabKeys.Chat}
            onChange={(key) => {
              setHashMap('td_tab', key);
            }}
          >
            <Tabs.TabPane
              key={TaskTabKeys.Chat}
              tab={<FormattedMessage id="conversations" />}
            >
              {convPane}
            </Tabs.TabPane>
            <Tabs.TabPane
              key={TaskDrawerTab.Approvals}
              tab={<FormattedMessage id="approvals" />}
            >
              {approvalsPane}
            </Tabs.TabPane>
          </Tabs>
        </ChatAndApprovalsWrapper>
      ) : (
        <></>
      )}
    </OuterWrapper>
  );
};
